import { Box, CircularProgress, LinearProgress } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Circle from '@src/components/Common/Circle';
import IconWrapper from '@src/components/Common/IconWrapper';
import useCallStatusFlags from '@src/hooks/call/useCallStatusFlags';
import {
  RECORDING_STATUS_POLLING_INTERVAL,
  useGetRecordingInfoQuery,
  useGetRecordingStatusQuery,
} from '@src/reducers/recordingAPI';
import CallMissedIcon from '@src/resources/icons/CallMissedIcon';
import { CallLite, RecordingStatus } from '@src/types/calls';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import ChatMessageAudio from './ChatMessageAudio';

const ChatMessageCall: React.FC<{
  call: CallLite;
}> = ({ call }) => {
  const { formatMessage } = useIntl();

  const { isCallTerminated, isCallMissed, isCallFailed, isCallRejected } =
    useCallStatusFlags(call.status);

  const [pollingInterval, setPollingInterval] = useState(
    RECORDING_STATUS_POLLING_INTERVAL
  );

  const { data: recordingStatus } = useGetRecordingStatusQuery(
    { callId: call.id },
    { skip: !isCallTerminated, pollingInterval }
  );

  useEffect(() => {
    const status = recordingStatus?.data?.status;
    if (
      status === RecordingStatus.COMPLETED ||
      status === RecordingStatus.FAILED ||
      status === RecordingStatus.NOT_AVAILABLE
    ) {
      setPollingInterval(0);
    }
  }, [recordingStatus?.data?.status]);

  const hasRecording =
    isCallTerminated &&
    recordingStatus?.data?.status === RecordingStatus.COMPLETED;

  const hasRecordingFailed =
    isCallTerminated &&
    recordingStatus?.data?.status === RecordingStatus.FAILED;

  const hasRecordingInProgress =
    isCallTerminated &&
    recordingStatus?.data?.status === RecordingStatus.IN_PROGRESS;

  const { data: recordingInfo } = useGetRecordingInfoQuery(
    { callId: call.id },
    { skip: !hasRecording }
  );

  const audioUrl = recordingInfo?.data?.url;

  return (
    <Box className="call-container">
      <Circle size={35} className="call-icon">
        <IconWrapper fontSize={23}>
          <CallMissedIcon />
        </IconWrapper>
      </Circle>

      <Box className="call-content">
        {isCallTerminated &&
          formatMessage({ id: 'Calling.CallTerminated.TitleLbl' })}
        {isCallMissed && formatMessage({ id: 'Calling.CallMissed.TitleLbl' })}
        {isCallFailed && formatMessage({ id: 'Calling.CallFailed.TitleLbl' })}
        {isCallRejected &&
          formatMessage({ id: 'Calling.CallRejected.TitleLbl' })}

        {hasRecording && audioUrl && (
          <ChatMessageAudio color="inherit" mediaUrl={audioUrl} />
        )}

        {hasRecordingFailed && (
          <Box className="call-recording-error">
            <ErrorOutlineIcon className="call-recording-error-icon" />
            <span className="call-recording-error-text">
              {formatMessage({ id: 'Recording.Failed.ContentLbl' })}
            </span>
          </Box>
        )}

        {hasRecordingInProgress && (
          <Box className="call-recording-in-progress">
            <div className="call-recording-in-progress-bar">
              <CircularProgress size={14} />
              <LinearProgress variant="indeterminate" />
            </div>
            <span className="call-recording-in-progress-text">
              {formatMessage({ id: 'Recording.InProgress.ContentLbl' })}
            </span>
          </Box>
        )}

        {!hasRecording && !hasRecordingFailed && !hasRecordingInProgress && (
          <Box className="call-content-descripton">
            {isCallTerminated &&
              formatMessage({ id: 'Calling.CallTerminated.ContentLbl' })}

            {isCallMissed &&
              formatMessage({ id: 'Calling.CallMissed.ContentLbl' })}

            {isCallFailed &&
              formatMessage({ id: 'Calling.CallFailed.ContentLbl' })}

            {isCallRejected &&
              formatMessage({ id: 'Calling.CallRejected.ContentLbl' })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessageCall;
